import {
  createPool,
  createPoolType,
  createPoolTypeCategory,
  deletePoolType,
  deletePoolTypeCategory,
  fetchPools,
  fetchPoolTypeCategories,
  fetchPoolTypeIds,
  fetchPoolTypes,
} from '@/api/admin-be';
import { catchError } from '@/helpers/toast';
import { defineStore } from 'pinia';
import type { CreatePool, CreatePoolType, CreatePoolTypeCategory } from '@/types/pools';

export const usePoolsStore = defineStore('pools', {
  state: () => ({
    poolTypes: null,
    poolTypeIds: null,
    typesOffset: 0,
    typesCount: 0,
    typesSize: 10,

    poolTypeCategories: null,
    categoriesOffset: 0,
    categoriesCount: 0,
    categoriesSize: 10,

    pools: null,
    poolsOffset: 0,
    poolsCount: 0,
    poolsSize: 10,
    filterPoolId: null,

    currentPoolId: null as null | number,
    currentPoolTypeId: null as null | number,
    loading: false,

    tab: 1,
  }),
  actions: {
    async fetchPools(filters?: any) {
      this.loading = true;
      return fetchPools({
        ...filters,
        offset: this.poolsOffset,
        pageSize: this.poolsSize,
        type: this.filterPoolId || undefined,
      })
        .then((response) => {
          this.pools = response.pools;
          this.poolsCount = response.count;
        })
        .catch((error) => catchError('Ошибка загрузки пулов', error))
        .finally(() => (this.loading = false));
    },

    async fetchPoolTypeIds() {
      return fetchPoolTypeIds().then((result) => {
        this.poolTypeIds = result;
      });
    },

    async fetchPoolTypes() {
      this.loading = true;
      await this.fetchPoolTypeIds();
      return fetchPoolTypes({ offset: this.typesOffset, size: this.typesSize })
        .then((response) => {
          this.poolTypes = response.poolTypes;
          this.typesCount = response.count;
        })
        .catch((error) => catchError('Ошибка загрузки типов пулов', error))
        .finally(() => (this.loading = false));
    },

    async fetchPoolTypeCategories() {
      if (!this.currentPoolTypeId) {
        this.poolTypeCategories = null;
        return;
      }
      this.loading = true;
      return fetchPoolTypeCategories(this.currentPoolTypeId, {
        offset: this.categoriesOffset,
        size: this.categoriesSize,
      })
        .then((response) => {
          this.poolTypeCategories = response.poolTypeCategories;
          this.categoriesCount = response.count;
        })
        .catch((error) => catchError('Ошибка загрузки категорий типа пулов', error))
        .finally(() => (this.loading = false));
    },

    async createPool(record: CreatePool) {
      this.loading = true;
      return createPool(record)
        .catch((error) => catchError('Ошибка создания пула', error))
        .finally(() => (this.loading = false));
    },

    async createPoolType(record: CreatePoolType) {
      this.loading = true;
      return createPoolType(record)
        .catch((error) => catchError('Ошибка создания типа', error))
        .finally(() => (this.loading = false));
    },

    async createPoolTypeCategory(id: number, record: CreatePoolTypeCategory) {
      this.loading = true;
      return createPoolTypeCategory(id, record)
        .catch((error) => catchError('Ошибка создания категории', error))
        .finally(() => (this.loading = false));
    },

    async deletePoolType(id: number) {
      this.loading = true;
      if (id === this.currentPoolTypeId) {
        this.currentPoolTypeId = null;
        this.poolTypeCategories = null;
      }
      return deletePoolType(id)
        .then(() => this.fetchPoolTypes())
        .catch((error) => catchError('Ошибка удаления типа', error))
        .finally(() => (this.loading = false));
    },

    async deletePoolTypeCategory(id: number) {
      this.loading = true;
      return deletePoolTypeCategory(id)
        .then(() => this.fetchPoolTypeCategories())
        .catch((error) => catchError('Ошибка удаления категории', error))
        .finally(() => (this.loading = false));
    },
  },
});
